import { lazy, Suspense, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { isPrivatePath } from '@/seo/privatePaths';
import { isLinksHostMode } from '@/seo/linksHost';

// The chat pulls in framer-motion and its own UI; none of it is needed to
// paint the page, so it loads once the page is idle or the visitor interacts.
const DimitrisChat = lazy(() => import('@/components/DimitrisChat'));

function useWhenIdle(): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (ready) return;
    const go = () => setReady(true);
    const events = ['pointerdown', 'keydown', 'scroll', 'touchstart'] as const;
    events.forEach((e) => window.addEventListener(e, go, { once: true, passive: true }));
    const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };
    const timer = w.requestIdleCallback ? w.requestIdleCallback(go, { timeout: 4000 }) : window.setTimeout(go, 3000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, go));
      if (!w.requestIdleCallback) window.clearTimeout(timer);
    };
  }, [ready]);
  return ready;
}

/**
 * The public chat bubble, on the marketing site only.
 *
 * Inside the app the sidebar Assistant answers questions, and on shared or
 * embedded reports the page belongs to the agency's client: a second, public
 * bubble in either place only competes with the report. The private-path list
 * is the same one the SSR edge uses, so a new app route is covered once.
 */
export function ConditionalDimitrisChat() {
  const location = useLocation();
  const ready = useWhenIdle();
  const path = location.pathname.replace(/\/+$/, '') || '/';
  // The bio link page (links.eyecaptain.io) is a short list of links; a bubble would cover them.
  if (isPrivatePath(path) || isLinksHostMode(window.location.hostname, window.location.search) || !ready) return null;
  return (
    <Suspense fallback={null}>
      <DimitrisChat context="public" />
    </Suspense>
  );
}
